import { Descendant, Editor, NodeEntry, Path, Element as SlateElement } from 'slate'

import { generateId } from 'core-components/helpers/generateId'
import {
  BLOCK_FORMATS,
  COLUMN_FORMATS,
  CustomEditor,
  TExtendedCustomElement,
  TTableCellElement,
  TTableElement,
  TTableRowElement,
} from 'core-components/library/richTextEditor/interface/custom-types'

import { ICreateCell, ICreateRow } from '../models/ITables'

export type TableCol = {
  cell: TTableCellElement
  isReal: boolean
  path: Path
  originPath: Path
  isInsert?: boolean
}

export const isTableCellElement = (
  element: TExtendedCustomElement
): element is TTableCellElement => {
  return element.type === BLOCK_FORMATS.tableCell
}

export const isTable = (editor: Editor) => {
  return Editor.above(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.table,
  })
}

export const createTable = (columns: number, rows: number): TTableElement => {
  const rowNodes = [...new Array(rows)].map(() => createRow({ columns }))

  return {
    type: BLOCK_FORMATS.table,
    children: rowNodes,
  }
}

export const createRow = (rowData: ICreateRow): TTableRowElement => {
  const { columns, rowspan = 1, colspan = 1 } = rowData
  const cellNodes = [...new Array(columns)].map(() => createCell({ rowspan, colspan }))

  return {
    type: BLOCK_FORMATS.tableRow,
    children: cellNodes,
  }
}

export const createCell = (cellData: ICreateCell): TTableCellElement => {
  const { elements, width, rowspan = 1, colspan = 1 } = cellData
  const paragraph: Descendant = { type: BLOCK_FORMATS.paragraph, children: [{ text: '' }] }
  const childs: Descendant[] = elements || [paragraph]

  return {
    key: generateId('cell'),
    type: BLOCK_FORMATS.tableCell,
    width,
    rowspan,
    colspan,
    children: childs,
  }
}

export const createColumnsRow = (format: COLUMN_FORMATS): TTableRowElement => {
  switch (format) {
    case COLUMN_FORMATS.half:
      return {
        type: BLOCK_FORMATS.tableRow,
        children: [createCell({}), createCell({ width: '50%' })],
      }
    case COLUMN_FORMATS.twoOne:
      return {
        type: BLOCK_FORMATS.tableRow,
        children: [createCell({}), createCell({ width: '33%' })],
      }
    case COLUMN_FORMATS.oneTwo:
      return {
        type: BLOCK_FORMATS.tableRow,
        children: [createCell({ width: '33%' }), createCell({})],
      }
    case COLUMN_FORMATS.threeOfOne:
      return {
        type: BLOCK_FORMATS.tableRow,
        children: [
          createCell({ width: '33%' }),
          createCell({ width: '33%' }),
          createCell({ width: '33%' }),
        ],
      }
    default:
      return {
        type: BLOCK_FORMATS.tableRow,
        children: [createCell({})],
      }
  }
}

const splitTable = (table: NodeEntry, editor: CustomEditor) => {
  const tableDepth = table[1].length
  const nodes = Editor.nodes(editor, {
    at: table[1],
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
  })

  const cells = [] as { cell: TTableCellElement; path: Path; realPath: Path }[]
  for (const node of nodes) {
    const [cell, path] = node as [TTableCellElement, Path]
    cells.push({
      cell,
      path,
      realPath: [...path],
    })
  }
  const gridTable: TableCol[][] = []
  for (let i = 0; i < cells.length; i++) {
    const { cell, path, realPath } = cells[i]
    const rowspan = cell.rowspan || 1
    const colspan = cell.colspan || 1
    const y = path[tableDepth]
    let x = path[tableDepth + 1]
    if (!gridTable[y]) {
      gridTable[y] = []
    }
    while (gridTable[y][x]) {
      x++
    }
    for (let j = 0; j < rowspan; j++) {
      for (let k = 0; k < colspan; k++) {
        const _y = y + j
        const _x = x + k
        if (!gridTable[_y]) {
          gridTable[_y] = []
        }
        gridTable[_y][_x] = {
          cell,
          path: [...realPath.slice(0, tableDepth), _y, _x],
          isReal: (rowspan === 1 && colspan === 1) || (_y === y && _x === x),
          originPath: path,
        }
      }
    }
  }
  return gridTable
}

export const getStartCell = (editor: CustomEditor) => {
  const [startCell] = Editor.nodes(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
  })
  return startCell
}

export const getGridTable = (editor: CustomEditor) => {
  const [table] = Array.from(
    Editor.nodes(editor, {
      match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.table,
    })
  )

  return {
    gridTable: splitTable(table, editor),
    xIndex: table[1].length + 1,
    yIndex: table[1].length,
  }
}

export const getCol = (
  gridTable: TableCol[][],
  match?: (node: TableCol) => boolean
): TableCol[] => {
  const result: TableCol[] = []
  gridTable.forEach((row) => {
    row.forEach((col: TableCol) => {
      if (match && match(col)) {
        result.push(col)
      }
    })
  })
  return result
}
